/**
 * WS-3 Workspaces (UID-48b, docs/design/screens.md §8/§9). Shaped
 * field-for-field against the FROZEN `GET /api/workspaces` +
 * `GET /api/workspaces/:id` contract the software-engineer shipped for
 * UID-48a — this is not a guess, it's the real, merged BFF shape, so
 * `mockWorkspacesService` (see `useWorkspaces.ts`) is a signature-
 * compatible drop-in for a real `apiClient`-backed implementation.
 */

export type WorkspaceVisibility = 'private' | 'account'
export type WorkspaceRole = 'owner' | 'member'

/** List-row projection (`GET /api/workspaces`). Private workspaces the
 * caller doesn't belong to are simply ABSENT from this list — never
 * present-with-a-lock — per the contract's deny-by-default framing
 * ("private non-member ones are NOT listed"). */
export interface WorkspaceSummary {
  readonly id: string
  readonly name: string
  readonly visibility: WorkspaceVisibility
  /**
   * UID-393 — the project's description, as the BFF now sends it on this list
   * projection (`openapi.yaml` `WorkspaceSummary.description`).
   *
   * REQUIRED AND NULLABLE (`string | null`), NOT optional (`description?:`) —
   * which makes it the odd one out against `partCount`/`bomCount`/
   * `membersPreview` below, deliberately. Those three are DERIVED at read time
   * and membership-gated, so the BFF genuinely omits them (ADR-0114 D2/D6/D7)
   * and absence carries meaning. `description` is a plain owned column on the
   * workspace row: never derived, never gated, so it is present on every
   * summary the BFF emits — the same discipline `name` follows. `null` means
   * "no description set" and is never coerced to `''`.
   *
   * Consumers therefore branch on NULL/emptiness, not on presence: see
   * `WorkspaceCard` / `WorkspaceListRow`, which render the line only for a
   * non-null, non-blank string and render NOTHING (no placeholder, no empty
   * node) otherwise.
   */
  readonly description: string | null
  readonly memberCount: number
  readonly myRole: WorkspaceRole | null
  readonly createdAt: string
  readonly updatedAt: string
  /**
   * UID-347/348 contents summary (`openapi.yaml` `WorkspaceSummary`, ADR-0114).
   * OPTIONAL, and **absence is never a zero** — the BFF omits both fields when
   * the caller is not an ACTIVE MEMBER (D6), on the embedded project-chip
   * projections that reuse this schema (D2), and when the derivation itself
   * failed (D7, fail-soft). An empty project the caller IS a member of returns
   * an explicit `0`. Consumers must therefore branch on presence, never
   * `?? 0` — see `WorkspaceCard`.
   */
  readonly partCount?: number
  readonly bomCount?: number
  /**
   * UID-374 members preview (`openapi.yaml` `WorkspaceMemberPreview`, ADR-0114).
   * The first up-to-3 members, for the card/row avatar stack the comp renders
   * (`Fictiv Platform.dc.html:1638` grid / `:1660` list — the source of the avatar-stack
   * requirement). OPTIONAL under the SAME discipline as the counts above — the BFF
   * omits it when the caller is not an ACTIVE MEMBER (D6), on the embedded
   * project-chip projections that reuse this schema (D2), and when the identity
   * derivation failed (D7, fail-soft).
   * **Absence is never "no members"** — a project the caller belongs to always has
   * at least the caller, so an empty array never comes back. Consumers must branch
   * on presence and fall back to `memberCount`, never render a zero-avatar stack.
   *
   * THE CAP OF 3 IS AN API-PAYLOAD DECISION, NOT THE COMP'S: those card/row stacks
   * bind `p.members` UNCAPPED (`projectCards` at `:8762-8765` applies no `.slice`;
   * the `hint-placeholder-count="3"` there is placeholder metadata, not a cap). The
   * "3 avatars + overflow chip" pattern is the workspace DETAIL header only (`:9965`,
   * chip at `:2591`), fed by `GET /workspaces/:id`'s full member list.
   *
   * UID-375 resolved the card/row visual: render `AvatarStack` with `max={3}` from
   * this (already-capped-at-3) array, and pass `totalCount={memberCount}` so the
   * `+N` overflow badge reflects the TRUE total rather than
   * `membersPreview.length`, which can never exceed 3 and so could never reveal
   * overflow on its own.
   *
   * The ARRAY itself is intentionally not `readonly` (unlike `Workspace.members`) for
   * ONE narrow reason: `AddToProjectModal.test.tsx`'s fixture annotates a literal as
   * this interface (~line 8) and then passes it where the GENERATED
   * `components['schemas']['WorkspaceSummary']` is expected; openapi-typescript emits
   * mutable arrays, and under `exactOptionalPropertyTypes` a `readonly T[]` is not
   * assignable to those (6 × TS2375). No PRODUCTION code forces this — the parts seam
   * (`PartWorkspaceLink.workspace`, `AddToProjectModal.tsx`) typechecks fine either
   * way. Retyping that fixture would let this go back to `readonly`.
   */
  readonly membersPreview?: WorkspaceMemberPreview[]
}

/** The MINIMAL member projection on `WorkspaceSummary.membersPreview` (UID-374) —
 * deliberately narrower than {@link WorkspaceMember}: no `workspaceRole`, no
 * `email`. Those stay on `GET /api/workspaces/:id`. */
export interface WorkspaceMemberPreview {
  readonly userId: string
  readonly displayName: string
  readonly avatarUrl: string | null
}

export interface WorkspaceMember {
  readonly userId: string
  readonly workspaceRole: WorkspaceRole
  readonly displayName: string
  readonly email: string
  readonly avatarUrl: string | null
  readonly joinedAt: string
}

/**
 * The caller's access level when they have FULL access to a single
 * workspace (`GET /api/workspaces/:id`). Distinct from `'redacted'`
 * (below) — a full-access response carries every `Workspace` field; a
 * redacted one carries almost none of them (see `WorkspaceRedactedStub`).
 */
export type WorkspaceFullAccess = 'owner' | 'member' | 'account'

export type WorkspaceJoinRequestStatus = 'pending' | null

export interface Workspace {
  readonly id: string
  readonly name: string
  readonly visibility: WorkspaceVisibility
  readonly description: string | null
  readonly access: WorkspaceFullAccess
  readonly memberCount: number
  readonly myRole: WorkspaceRole | null
  readonly members: readonly WorkspaceMember[]
  /** True only when there's no live join request — gates the "Request
   * access" CTA. Always `false` on a full-access `Workspace` in practice
   * (you already have access), kept on the shape because both `Workspace`
   * and `WorkspaceRedactedStub` need it for a uniform discriminated-union
   * read on `access`. */
  readonly canRequestAccess: boolean
  readonly myJoinRequestStatus: WorkspaceJoinRequestStatus
  readonly createdAt: string
  readonly updatedAt: string
}

/**
 * The REDUCED redacted-view shape (security ruling, UID-48a-app 7e6ff4d —
 * 2nd-pass tightening of the contract this vertical was originally built
 * against). `GET /api/workspaces/:id` and `DELETE
 * /api/workspaces/:id/members/:userId` both return `Workspace |
 * WorkspaceRedactedStub`, discriminated on `access`. Deliberately carries
 * ONLY these five fields — no `name`, `memberCount`, `description`,
 * `members`, or timestamps. Earlier this vertical modeled "redacted" as a
 * `Workspace` with those fields nulled out; that's no longer how the wire
 * shape works, so the FE must not assume ANY field beyond these five
 * exists when `access === 'redacted'`.
 */
export interface WorkspaceRedactedStub {
  readonly id: string
  readonly visibility: WorkspaceVisibility
  readonly access: 'redacted'
  /** True only when there's no live join request — gates the "Request
   * access" CTA (screens.md §8's overlay). */
  readonly canRequestAccess: boolean
  readonly myJoinRequestStatus: WorkspaceJoinRequestStatus
}

/** The discriminated union `GET /api/workspaces/:id` (and the member-
 * removal DELETE) actually returns. Branch on `access === 'redacted'`
 * (`isRedactedWorkspace`) before reading any field beyond `id`/
 * `visibility`/`access`/`canRequestAccess`/`myJoinRequestStatus`. */
export type WorkspaceOrRedacted = Workspace | WorkspaceRedactedStub

export function isRedactedWorkspace(
  workspace: WorkspaceOrRedacted,
): workspace is WorkspaceRedactedStub {
  return workspace.access === 'redacted'
}

/** The REAL `WorkspaceJoinRequest.status` vocabulary (UID-106 — wider than
 * this vertical's original `'pending'`-only assumption): the idempotent
 * `POST /:id/request-access` can echo back a PRIOR request that has since
 * been decided or withdrawn, not only a fresh pending one. */
export type WorkspaceJoinRequestDecision = 'pending' | 'approved' | 'denied' | 'withdrawn'

export interface WorkspaceJoinRequest {
  readonly id: string
  readonly workspaceId: string
  readonly requesterUserId: string
  readonly status: WorkspaceJoinRequestDecision
  readonly decidedByUserId: string | null
  readonly decidedAt: string | null
  readonly createdAt: string
}

/** `POST /api/workspaces` body (new-workspace modal, screens.md §9). */
export interface CreateWorkspaceRequest {
  readonly name: string
  readonly visibility: WorkspaceVisibility
  readonly description?: string
}

/** `PATCH /api/workspaces/:id` body (UID-177 — persist Project rename). `name`
 * is required; `description` is optional — omit to leave unchanged, or send
 * `null`/'' to clear it (matches the contract's `type: [string, 'null']`). */
export interface UpdateWorkspaceRequest {
  readonly name: string
  readonly description?: string | null
}

/** `POST /api/workspaces/:id/members` body (assign/share modal, screens.md §8). */
export interface AddWorkspaceMemberRequest {
  readonly userId: string
  readonly workspaceRole: WorkspaceRole
}

export class WorkspaceNotFoundError extends Error {
  constructor(workspaceId: string) {
    super(`Workspace "${workspaceId}" was not found`)
    this.name = 'WorkspaceNotFoundError'
  }
}
